import { useEffect, useState } from "react";
import axios from "axios";

export interface PickupStore {
  id: string;
  name: string;
  location: string;
  street_address: string;
  postal_code: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  opening_hours: {
    open_now: boolean;
    weekday_text: string[];
  };
  pickup_store: number;
  [key: string]: any;
}

/**
 * Custom React hook to manage pickup store selection and loading state.
 *
 * This hook fetches a list of pickup stores from a remote API, filters them to include only stores
 * marked as pickup stores, and manages the currently selected store. It also provides loading and error
 * states, as well as a method to reload the store data.
 *
 * @returns An object containing:
 * - `stores`: Array of available pickup stores.
 * - `selectedStore`: The currently selected pickup store, or `null` if none is selected.
 * - `setSelectedStore`: Function to manually set the selected store.
 * - `loading`: Boolean indicating if the store data is currently being loaded.
 * - `error`: Error message string if loading failed, or `null` if no error.
 * - `reload`: Function to reload the store data from the API.
 *
 * @example
 * const { stores, selectedStore, setSelectedStore, loading, error, reload } = usePickupStores();
 */
export const usePickupStores = () => {
  const [stores, setStores] = useState<PickupStore[]>([]);
  const [selectedStore, setSelectedStore] = useState<PickupStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(window._env_!.REACT_APP_PICKUP_STORE_API!, {
        headers: {
          Authorization: window._env_!.REACT_APP_PICKUP_STORE_AUTH!,
        },
      });

      if (response.data?.success && Array.isArray(response.data.data)) {
        const pickupOnly = response.data.data.filter((store: PickupStore) => store.pickup_store === 1);
        setStores(pickupOnly);
        if (!selectedStore && pickupOnly.length > 0) {
          setSelectedStore(pickupOnly[0]);
        }
      } else {
        setError("Fehler beim Laden der Store-Daten.");
      }
    } catch (err: any) {
      setError(err.message || "Unbekannter Fehler beim Laden der Stores.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStores();
  }, []);

  return {
    stores,
    selectedStore,
    setSelectedStore,
    loading,
    error,
    reload: loadStores,
  };
};
